/**
 * Solo Belge Yönetimi — Solo planı için tam-sayfa belge görünümü.
 *
 * Solo'da "manuel belge" kavramı YOK. Bu sayfa yalnızca harcamalara ekli
 * fişleri/faturaları listeler. Filtreler:
 *   • Arama (dosya adı, işletme, açıklama)
 *   • Yıl · Ay · Gün (harcama tarihine göre)
 *   • Kategori (Market, Yakıt, Fatura: X, Kırtasiye/Ofis vb.)
 *
 * Belgeye tıklayınca `BelgeOnizlemeModal` açılır (zoom in/out + indir).
 * Yeni sekmede açma yok — her şey modal içinde.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Input } from './ui/input';
import {
  FileTextIcon, SearchIcon, FilterIcon, XCircleIcon, PaperclipIcon,
  BuildingIcon, WalletIcon, CalendarIcon, TagIcon, ImageIcon, SendIcon
} from 'lucide-react';
import BelgeOnizlemeModal from './BelgeOnizlemeModal';
import SoloDocumentsSendModal from './SoloDocumentsSendModal';
import { Button } from './ui/button';
import { MailIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TR_MONTHS = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const fmtCurrency = (amount, currency = 'TRY') => {
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount || 0);
  } catch { return `${amount} ${currency}`; }
};

const fmtBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageFile = (file) => {
  const t = (file?.type || '').toLowerCase();
  const n = (file?.name || '').toLowerCase();
  return t.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(n);
};
const isPdfFile = (file) => {
  const t = (file?.type || '').toLowerCase();
  const n = (file?.name || '').toLowerCase();
  return t.includes('pdf') || /\.pdf$/i.test(n);
};

const SoloDocumentsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'company' | 'personal'
  const [previewFile, setPreviewFile] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  // Yalnızca `attached_file` sahibi olan solo harcamaları çek.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const resp = await axios.get(`${BACKEND_URL}/api/solo/expenses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const allExpenses = resp.data?.expenses || resp.data || [];
        const withFiles = allExpenses.filter(e => e.attached_file && e.attached_file.url);
        if (!cancelled) setRows(withFiles);
      } catch (_) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Facet listeleri — filtrede seçilebilecek benzersiz değerler.
  const facets = useMemo(() => {
    const years = new Set();
    const months = new Set();
    const days = new Set();
    const categories = new Set();
    rows.forEach(r => {
      if (r.date) {
        const [y, m, d] = r.date.split('-');
        if (y) years.add(y);
        if (m) months.add(String(parseInt(m, 10)));
        if (d) days.add(String(parseInt(d, 10)));
      }
      if (r.category) categories.add(r.category);
    });
    return {
      years: [...years].sort().reverse(),
      months: [...months].sort((a, b) => Number(a) - Number(b)),
      days: [...days].sort((a, b) => Number(a) - Number(b)),
      categories: [...categories].sort(),
    };
  }, [rows]);

  // Uygulanan filtreleri satırlar üzerine geçir.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (yearFilter !== 'all') {
        const y = (r.date || '').slice(0, 4);
        if (y !== yearFilter) return false;
      }
      if (monthFilter !== 'all') {
        const m = String(parseInt((r.date || '').slice(5, 7), 10));
        if (m !== monthFilter) return false;
      }
      if (dayFilter !== 'all') {
        const d = String(parseInt((r.date || '').slice(8, 10), 10));
        if (d !== dayFilter) return false;
      }
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
      if (typeFilter !== 'all' && (r.expense_type || '') !== typeFilter) return false;
      if (q) {
        const hay = [
          r.attached_file?.name, r.supplier, r.description, r.category,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, yearFilter, monthFilter, dayFilter, categoryFilter, typeFilter]);

  const hasActiveFilter = yearFilter !== 'all' || monthFilter !== 'all'
    || dayFilter !== 'all' || categoryFilter !== 'all' || typeFilter !== 'all' || !!search;

  const clearFilters = () => {
    setYearFilter('all');
    setMonthFilter('all');
    setDayFilter('all');
    setCategoryFilter('all');
    setTypeFilter('all');
    setSearch('');
  };

  return (
    <div className="min-h-full bg-gray-50" data-testid="solo-documents-page">
      {/* Top banner */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Belgeler</h1>
            <p className="text-sm text-gray-500 mt-1">
              Harcamalarınıza eklediğiniz tüm fişler ve faturalar tek yerde. Yıl / Ay / Gün / Kategori bazında filtreleyin.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 text-xs font-medium">
              <FileTextIcon className="w-4 h-4" />
              {filtered.length} belge
            </div>
            <Button
              onClick={() => setSendModalOpen(true)}
              disabled={filtered.length === 0}
              className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300"
              data-testid="solo-docs-send-btn"
            >
              <MailIcon className="w-4 h-4 mr-1.5" />
              Mali Müşavire Gönder
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* Filtre paneli */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <FilterIcon className="w-4 h-4 text-gray-500" />
              Filtreler
            </div>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                data-testid="solo-documents-clear-filters"
              >
                <XCircleIcon className="w-3.5 h-3.5" /> Filtreleri temizle
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            {/* Tip — Şirket / Şahsi */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Tip</label>
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
                data-testid="solo-documents-type-filter"
              >
                <option value="all">Tümü</option>
                <option value="company">Şirket</option>
                <option value="personal">Şahsi</option>
              </select>
            </div>
            {/* Yıl */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Yıl</label>
              <select
                value={yearFilter}
                onChange={e => setYearFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
                data-testid="solo-documents-year-filter"
              >
                <option value="all">Tüm yıllar</option>
                {facets.years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {/* Ay */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Ay</label>
              <select
                value={monthFilter}
                onChange={e => setMonthFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
                data-testid="solo-documents-month-filter"
              >
                <option value="all">Tüm aylar</option>
                {facets.months.map(m => (
                  <option key={m} value={m}>{TR_MONTHS[Number(m)]}</option>
                ))}
              </select>
            </div>
            {/* Gün */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Gün</label>
              <select
                value={dayFilter}
                onChange={e => setDayFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
                data-testid="solo-documents-day-filter"
              >
                <option value="all">Tüm günler</option>
                {facets.days.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            {/* Kategori */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Kategori</label>
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
                data-testid="solo-documents-category-filter"
              >
                <option value="all">Tüm kategoriler</option>
                {facets.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Arama */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Ara</label>
              <div className="relative">
                <SearchIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Dosya, işletme, açıklama..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 h-10 text-sm"
                  data-testid="solo-documents-search"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Belgeler listesi — kart grid */}
        {loading ? (
          <Card className="p-12 text-center text-gray-500">Yükleniyor...</Card>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center" data-testid="solo-documents-empty">
            <FileTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              {hasActiveFilter ? 'Bu filtrelere uyan belge yok.' : 'Henüz hiç belge yüklemediniz.'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Harcama eklerken fiş/fatura fotoğrafı yüklerseniz burada listelenir.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="solo-documents-grid">
            {filtered.map(row => {
              const f = row.attached_file;
              const image = isImageFile(f);
              const pdf = isPdfFile(f);
              return (
                <Card
                  key={row.id}
                  className="p-4 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group"
                  onClick={() => setPreviewFile(f)}
                  data-testid={`solo-document-card-${row.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Thumbnail */}
                    {image ? (
                      <img
                        src={f.url}
                        alt={f.name}
                        className="w-16 h-16 object-cover rounded-md border border-gray-200 flex-shrink-0 bg-white"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fb = e.currentTarget.nextElementSibling;
                          if (fb) fb.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-16 h-16 rounded-md border flex items-center justify-center flex-shrink-0 ${
                        pdf ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
                      }`}
                      style={{ display: image ? 'none' : 'flex' }}
                    >
                      {pdf ? (
                        <div className="flex flex-col items-center leading-none">
                          <FileTextIcon className="w-7 h-7 text-red-500" />
                          <span className="text-[10px] font-bold text-red-600 mt-0.5">PDF</span>
                        </div>
                      ) : (
                        <ImageIcon className="w-7 h-7 text-gray-400" />
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <PaperclipIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <p className="text-sm font-medium text-gray-900 truncate" title={f.name}>
                          {f.name || 'Belge'}
                        </p>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 mb-2">
                        <span>{fmtBytes(f.size)}</span>
                        {f.type && (
                          <>
                            <span>·</span>
                            <span className="uppercase">{(f.type.split('/')[1] || f.type).slice(0, 4)}</span>
                          </>
                        )}
                      </div>

                      {/* Harcama bilgileri */}
                      <div className="pt-2 border-t border-gray-100 space-y-1 text-xs">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <CalendarIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span>{row.date}</span>
                          {row.expense_type === 'company' ? (
                            <span className="ml-auto px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">
                              <BuildingIcon className="inline w-2.5 h-2.5 mr-0.5" />Şirket
                            </span>
                          ) : (
                            <span className="ml-auto px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px]">
                              <WalletIcon className="inline w-2.5 h-2.5 mr-0.5" />Şahsi
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <BuildingIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="truncate" title={row.supplier}>
                            {row.supplier || '—'}
                          </span>
                        </div>
                        {row.category && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <TagIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{row.category}</span>
                          </div>
                        )}
                        <div className="text-sm font-semibold text-gray-900 pt-1">
                          {fmtCurrency(row.amount, row.currency)}
                        </div>

                        {/* Gönderim izi — belge daha önce mali müşavire
                            gönderildiyse burada rozet olarak görünür. */}
                        {f.last_sent_at && (
                          <div
                            className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md bg-teal-50 border border-teal-200 text-[11px] text-teal-800"
                            data-testid={`solo-document-sent-badge-${row.id}`}
                            title={`Gönderildi: ${f.last_sent_to}\n${new Date(f.last_sent_at).toLocaleString('tr-TR')}`}
                          >
                            <SendIcon className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">
                              {new Date(f.last_sent_at).toLocaleDateString('tr-TR', {
                                day: '2-digit', month: 'short', year: 'numeric',
                              })} · {f.last_sent_to}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {previewFile && (
        <BelgeOnizlemeModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}

      <SoloDocumentsSendModal
        open={sendModalOpen}
        filteredCount={filtered.length}
        filterState={{
          yearFilter,
          monthFilter,
          dayFilter,
          categoryFilter,
          typeFilter,
          search,
        }}
        onClose={() => setSendModalOpen(false)}
      />
    </div>
  );
};

export default SoloDocumentsPage;
